import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Textarea } from "@shared/ui/textarea";
import { Switch } from "@shared/ui/switch";
import { Separator } from "@shared/ui/separator";
import { Badge } from "@shared/ui/badge";
import { Calendar } from "@shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { cn } from "@shared/lib/utils";
import {
  CalendarIcon,
  Gauge,
  DollarSign,
  Wrench,
  CreditCard,
  AlertTriangle,
  FileText,
  Gift,
  RefreshCw,
  Info,
  Plus,
  Trash2,
  Droplets,
  Zap,
  Flame,
  Waves,
  ArrowRight,
  Calculator,
  LayoutTemplate,
  Check,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { Progress } from "@shared/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { BillOverview } from "./components/BillOverview";
import { useBillCorrectionDetails, useBillTemplates, usePatchBillCorrection } from "../hook";
import { FormField, FormService } from "@shared/services/FormServices";
import { normalizeDate } from "@shared/utils/dateUtils";
import { DynamicForm } from "@shared/components/DynamicForm";
import ConsumptionCorrectionForm from "./components/MeterConsumptionForm";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";

interface CorrectionDetailsProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

interface CorrectionProps {
  onSave: (data: any) => void;
  isSaved: boolean;
  billDetails?: any;
  correctionId?: string;
  savedData?: any;
}

// Mapping from numeric keys to type names
const CORRECTION_TYPE_MAP: Record<number, string> = {
  0: "consumption",
  1: "service",
  2: "penalty",
  3: "data",
  4: "template",
};

export default function CorrectionDetails({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
}: CorrectionDetailsProps) {
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [editingSteps, setEditingSteps] = useState<string[]>([]); // Track which saved forms are being edited
  
  const selectedBill = stepHelpers.getStepData(0);
  const selectedCorrectionTypes = stepHelpers.getStepData(1);
  const savedCorrectionData = stepHelpers.getStepData(2) || {};
  
  const { data: billDetails } = useBillCorrectionDetails(
    selectedBill?.selectedBill?.id
  );
  
  // Map numeric keys to type names
  const selectedTypesFromData =
    selectedCorrectionTypes?.selectedCorrectionTypes || [];
  const selectedTypes = selectedTypesFromData.map(
    (key: number) => CORRECTION_TYPE_MAP[key]
  );

  // Initialize completed steps from saved data on mount
  useEffect(() => {
    const savedTypes = Object.keys(savedCorrectionData);
    setCompletedSteps(savedTypes);
  }, []);

  if (selectedTypes?.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <p className="text-gray-500">
          No correction types selected. Please go back and select at least one
          type.
        </p>
        <Button className="mr-auto" onClick={onPrevious}>
          Previous
        </Button>
      </div>
    );
  }

  // Define the order of correction types to maintain consistency
  const typeOrder = [
    "consumption",
    "tariff",
    "service",
    "payment",
    "penalty",
    "data",
    "rebate",
    "template",
    "cancel",
  ];

  // Sort selected types based on the defined order
  const sortedTypes = typeOrder.filter((type) => selectedTypes?.includes(type));

  const handleSave = (type: string, data: any) => {
    // Get existing step data
    const existingData = stepHelpers.getStepData(2) || {};
    
    // Merge with new data
    const updatedData = {
      ...existingData,
      [type]: data
    };
    
    // Save to stepHelper
    stepHelpers.setStepData(2, updatedData);
    
    // Add to completed steps if not already there
    if (!completedSteps.includes(type)) {
      setCompletedSteps([...completedSteps, type]);
    }
    
    // Remove from editing steps
    setEditingSteps(prev => prev.filter(t => t !== type));
    
    toast({ title: "Correction details saved successfully" });
  };

  const handleFormEdit = (type: string) => {
    // Mark this form as being edited (if it was previously saved)
    if (completedSteps.includes(type) && !editingSteps.includes(type)) {
      setEditingSteps(prev => [...prev, type]);
      // Remove from completed steps
      setCompletedSteps(prev => prev.filter(t => t !== type));
    }
  };

  const renderComponent = (type: string) => {
    // A form is saved if it's in completedSteps and NOT in editingSteps
    const isCurrentlySaved = completedSteps.includes(type) && !editingSteps.includes(type);
    
    const props = {
      onSave: (data: any) => handleSave(type, data),
      onFormEdit: () => handleFormEdit(type),
      isSaved: isCurrentlySaved,
      billDetails,
      correctionId: selectedBill?.correctionId,
      savedData: savedCorrectionData[type]
    };

    switch (type) {
      case "consumption":
        return <ConsumptionCorrectionForm {...props} />;
      case "service":
        return <ServiceChargeCorrection {...props} />;
      case "penalty":
        return <PenaltyCorrection {...props} />;
      case "data":
        return <DataCorrection {...props} />;
      case "template":
        return <TemplateCorrection {...props} />;
      default:
        return null;
    }
  };

  // Check if all selected types are completed (and not being edited)
  const allTypesCompleted = sortedTypes.every(type => 
    completedSteps.includes(type) && !editingSteps.includes(type)
  );

  return (
    <div className="h-[70vh] overflow-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Column */}
        <div className="lg:col-span-8 space-y-8">
          {sortedTypes.map((type) => (
            <div key={type} id={`correction-${type}`} className="scroll-mt-24">
              {renderComponent(type)}
            </div>
          ))}
        </div>

        {/* Right Column */}
        <aside className="col-span-4 sticky top-0 h-[100dvh] self-start">
          <div className="flex h-full flex-col gap-4 overflow-y-auto pr-2">
            <BillOverview bill={selectedBill?.selectedBill} />
            <CorrectionSummarySidePanel
              types={sortedTypes}
              completed={completedSteps}
              editing={editingSteps}
            />
          </div>
        </aside>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button 
          onClick={onNext}
          disabled={!allTypesCompleted}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function CorrectionSummarySidePanel({
  types,
  completed,
  editing,
}: {
  types: string[];
  completed: string[];
  editing: string[];
}) {
  const total = types.length;
  const done = completed.filter(type => !editing.includes(type)).length;
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  const getLabel = (type: string) => {
    switch (type) {
      case "consumption":
        return "Consumption";
      case "tariff":
        return "Tariff / Rate";
      case "service":
        return "Service Charges";
      case "payment":
        return "Payment";
      case "penalty":
        return "Penalty / Late Fee";
      case "data":
        return "Consumer Data";
      case "rebate":
        return "Rebate / Credit";
      case "template":
        return "Bill Template";
      case "cancel":
        return "Cancel & Rebill";
      default:
        return type;
    }
  };

  return (
    <Card className="border-blue-100 shadow-md overflow-hidden relative">
      <CardHeader className="bg-blue-50/50 border-b border-blue-100 p-2">
        <CardTitle className="">Correction Summary</CardTitle>
        <div className="flex items-center justify-between card-subheading-text mt-1">
          <span>Progress</span>
          <span>
            {progress}% ({done}/{total})
          </span>
        </div>
        <Progress value={progress} className="h-2 mt-2" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {types.map((type) => {
            const isDone = completed.includes(type) && !editing.includes(type);
            const isEditing = editing.includes(type);
            
            return (
              <div
                key={type}
                className="p-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "h-2.5 w-2.5 rounded-full ring-2 ring-offset-1",
                      isDone
                        ? "bg-green-500 ring-green-100"
                        : isEditing
                        ? "bg-yellow-500 ring-yellow-100"
                        : "bg-gray-300 ring-gray-100"
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isDone 
                        ? "text-gray-900" 
                        : isEditing
                        ? "text-yellow-700"
                        : "text-gray-500"
                    )}
                  >
                    {getLabel(type)}
                  </span>
                </div>
                {isDone && <Check className="h-4 w-4 text-green-600" />}
                {isEditing && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    Editing
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Update the interface
interface CorrectionProps {
  onSave: (data: any) => void;
  onFormEdit: () => void; // New prop
  isSaved: boolean;
  billDetails?: any;
  correctionId?: string;
  savedData?: any;
}
type ServiceChargeState = {
  key: string;
  label: string;
  isActive: boolean;
  fee: any;
};

type ApiServiceCharge = {
  key: string;
  chargeLabel: string;
  fee: number;
  utilityService: string;
};
// ServiceChargeCorrection component (updated)
function ServiceChargeCorrection({ 
  billDetails, 
  onSave, 
  onFormEdit,
  isSaved, 
  savedData,
  correctionId
}: CorrectionProps) {
  const {mutate: excludeServiceCharge} = usePatchBillCorrection();
  const [charges, setCharges] = useState<ServiceChargeState[]>([]);
  const [reason, setReason] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  /* --------------------------------------------
   * Initialize from savedData or billDetails
   * ------------------------------------------ */
  useEffect(() => {
    if (savedData) {
      // Populate from saved data
      setCharges(savedData.serviceCharges || []);
      setReason(savedData.reason || "");
    } else if (billDetails?.serviceCharges?.length) {
      // Initialize fresh from billDetails
      const mapped = billDetails.serviceCharges.map(
        (c: ApiServiceCharge) => ({
          key: c.key,
          label: c.key,
          isActive: true,
          fee: c.fee,
        })
      );
      setCharges(mapped);
    }
    setIsInitialized(true);
  }, [billDetails?.serviceCharges, savedData]);

  /* --------------------------------------------
   * Toggle include/exclude
   * ------------------------------------------ */
  const toggleCharge = (key: string) => {
    if (isInitialized && isSaved) {
      onFormEdit();
    }
    setCharges((prev) =>
      prev.map((c) =>
        c.key === key ? { ...c, isActive: !c.isActive } : c
      )
    );
  };

  const handleReasonChange = (value: string) => {
    if (isInitialized && isSaved) {
      onFormEdit();
    }
    setReason(value);
  };

  /* --------------------------------------------
   * Save Payload
   * ------------------------------------------ */
  const handleSave = () => {
    const payload = {
    serviceCharges: charges.map(
      ({ key, isActive }) => ({
        key,
        isActive,
        reason
      })
    ),
    // reason,
  };
    const data = {
      serviceCharges: charges,
      reason
    };
    excludeServiceCharge({
      id: correctionId,
      type: "service-charges",
      payload
    }, {
      onSuccess: () => onSave(data)
    })
  };

  return (
    <Card className="border-purple-100 shadow-sm overflow-hidden">
      <CardHeader className="bg-purple-50/50 border-b border-purple-100 p-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Wrench className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <CardTitle>Service Charge Correction</CardTitle>
            <p className="card-subheading-text">
              Exclude service charges from bill
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className=" space-y-4">
        {/* ---------------- Charges Cards ---------------- */}
        {charges.map((charge) => {
          const isExcluded = !charge.isActive;

          return (
            <div
              key={charge.key}
              className={cn(
                "p-4 border rounded-xl transition-all",
                isExcluded
                  ? "bg-red-50 border-red-200"
                  : "bg-white border-gray-200"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {charge.key}
                  </p>
                  <p className="text-xs text-gray-500">
                    Fees: {charge.fee}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Exclude</span>
                  <Switch
                    checked={isExcluded}
                    onCheckedChange={() => toggleCharge(charge.key)}
                  />
                </div>
              </div>

              {/* Excluded Note */}
              {isExcluded && (
                <div className="mt-2 flex items-start gap-2 text-xs text-red-700 bg-red-100/60 p-2 rounded-md">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <span>
                    This service charge will be excluded from the bill.
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {/* ---------------- Reason ---------------- */}
        <div className="pt-4">
          <Label className="text-gray-700">Reason<span className="text-red-500">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => handleReasonChange(e.target.value)}
            placeholder="Reason for excluding service charges..."
            className="mt-2 min-h-[80px]"
          />
        </div>

        {/* ---------------- Save ---------------- */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            disabled={!reason}
            onClick={handleSave}
            className={cn(
              "min-w-[140px]",
              isSaved
                ? "bg-green-600 hover:bg-green-700"
                : "bg-purple-600 hover:bg-purple-700"
            )}
          >
            {isSaved ? (
              <>
                <Check className="mr-2 h-4 w-4" /> Saved
              </>
            ) : (
              "Save Correction"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// PenaltyCorrection component (updated)
function PenaltyCorrection({ 
  billDetails, 
  onSave,
  onFormEdit,
  isSaved, 
  correctionId,
  savedData 
}: CorrectionProps) {
  const [isWaived, setIsWaived] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const isUpdatingRef = useRef(false);
  const currency = useCurrency();
  const { mutate: correctLateFee } = usePatchBillCorrection();
  
  const getFormFields = (): FormField[] => {
    const fields: FormField[] = [];

    // Only show adjust amount field when NOT waived
    if (!isWaived) {
      fields.push({
        name: "adjustedAmount",
        label: "Adjust Amount",
        type: "number",
        required: !isWaived,
        placeholder: "Enter new penalty amount",
        className: "col-span-1",
      });
    }

    // Reason is always required
    fields.push({
      name: "reason",
      label: "Reason for Waiver/Adjustment",
      type: "textarea",
      required: true,
      placeholder: "e.g., Customer request approved by manager...",
      className: "col-span-1",
    });

    return fields;
  };

  const form = FormService.createForm({
    fields: getFormFields(),
    mode: "onChange",
    validateOnMount: false,
  });

  // Initialize from saved data
  useEffect(() => {
    isUpdatingRef.current = true;
    
    if (savedData) {
      setIsWaived(savedData.isWaived || false);
      if (savedData.adjustedAmount !== undefined) {
        form.setValue("adjustedAmount", savedData.adjustedAmount, { shouldValidate: false });
      }
      if (savedData.reason) {
        form.setValue("reason", savedData.reason, { shouldValidate: false });
      }
    }
    
    isUpdatingRef.current = false;
    
    // Mark as initialized after a short delay
    setTimeout(() => {
      setIsInitialized(true);
    }, 100);
  }, [savedData]);

  // Update form fields when isWaived changes (but don't trigger during initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    // Reset the form with new fields structure
    const newFields = getFormFields();
    
    // If waived, clear the adjustedAmount field
    if (isWaived) {
      form.setValue("adjustedAmount", undefined, { shouldValidate: false });
    }
  }, [isWaived]);

  const handleToggleWaive = (checked: boolean) => {
    if (isInitialized && isSaved) {
      onFormEdit();
    }
    setIsWaived(checked);
    
    // If waiving, set adjusted amount to 0
    if (checked) {
      isUpdatingRef.current = true;
      form.setValue("adjustedAmount", 0, { shouldValidate: false });
      isUpdatingRef.current = false;
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (isUpdatingRef.current) {
      return;
    }
    if (isInitialized && isSaved) {
      onFormEdit();
    }
  };

  const handleSave = async () => {
    const isValid = await FormService.validateForm(form);
    
    if (isValid) {
      const formData = form.getValues();
      
      const data = {
        isWaived,
        adjustedAmount: isWaived ? 0 : formData.adjustedAmount,
        reason: formData.reason
      };
      
      correctLateFee({
        id: correctionId,
        type: 'late-fees',
        payload: {
          corrected_late_fee: data.adjustedAmount,
          waive_completely: data.isWaived,
          reason: data.reason
        }
      }, {
        onSuccess: () => onSave(data)
      });
    }
  };

  return (
    <Card className="border-red-100 shadow-sm overflow-hidden">
      <CardHeader className="bg-red-50/50 border-b border-red-100 p-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <CardTitle>Penalty/Late Fee Correction</CardTitle>
            <p className="text-xs text-gray-600 font-normal">
              Waive or adjust penalties
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="">
        <div className="flex items-center justify-between bg-red-50 rounded-lg border border-red-100 mb-6 p-4">
          <div>
            <div className="text-sm font-medium text-red-900">
              Current Late Fee
            </div>
            <div className="text-2xl font-bold text-red-700">
              {currency} {billDetails?.lateFee?.fee || "0"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              id="waive-penalty" 
              checked={isWaived}
              onCheckedChange={handleToggleWaive}
            />
            <Label htmlFor="waive-penalty" className="font-medium">
              Waive Completely
            </Label>
          </div>
        </div>

        <DynamicForm
          fields={getFormFields()}
          form={form}
          gridColumns={1}
          responsiveColumns={{ sm: 1, md: 1, lg: 1 }}
          config={{
            layout: "vertical",
            spacing: "medium",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={(field, value) => handleFieldChange(field, value)}
        />

        {isWaived && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-2 text-sm text-green-700">
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Late fee will be completely waived (set to ₹0)</span>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <Button
            onClick={handleSave}
            className={cn(
              "min-w-[140px] transition-all duration-200",
              isSaved
                ? "bg-green-600 hover:bg-green-700 text-white shadow-md"
                : "bg-red-600 hover:bg-red-700 text-white shadow-sm"
            )}
          >
            {isSaved ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Correction"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface DataField {
  id: string;
  label: string;
  original: string;
  new: string;
}
// DataCorrection component (updated)
function DataCorrection({ 
  billDetails, 
  onSave,
  onFormEdit,
  isSaved, 
  correctionId,
  savedData 
}: CorrectionProps) {
  if (!billDetails) {
    return null;
  }
  
  const [reason, setReason] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [dataFields, setDataFields] = useState<DataField[]>([
    { id: "firstName", label: "First Name", original: billDetails?.consumerDetails?.firstName, new: "" },
    { id: "lastName", label: "Last Name", original: billDetails?.consumerDetails?.lastName, new: "" },
    {
      id: "phone",
      label: "Phone Number",
      original: billDetails?.consumerDetails?.contactNumber,
      new: "",
    },
  ]);
  
  const { mutate: correctData } = usePatchBillCorrection();
  
  // Initialize from saved data
  useEffect(() => {
    if (savedData) {
      setReason(savedData.reason || "");
      setDataFields(prev => prev.map(field => ({
        ...field,
        new: savedData[field.id] || ""
      })));
    }
    setIsInitialized(true);
  }, [savedData]);
  
  const handleUpdate = (id: string, value: string) => {
    if (isInitialized && isSaved) {
      onFormEdit();
    }
    setDataFields(
      dataFields.map((f) => (f.id === id ? { ...f, new: value } : f))
    );
  };

  const handleReasonChange = (value: string) => {
    if (isInitialized && isSaved) {
      onFormEdit();
    }
    setReason(value);
  };
  
  const handleSave = () => {
    const payload = dataFields.reduce<Record<string, string>>((acc, field) => {
      if (!field.new) return acc;
      acc[field.id] = field.new;
      return acc;
    }, {});
    payload.reason = reason.trim();

    const data = {
      ...payload
    };

    correctData({
      id: correctionId,
      type: 'consumer-data',
      payload
    }, {
      onSuccess: () => onSave(data)
    });
  };

  const columns: TableColumn<DataField>[] = [
    {
      key: "label",
      header: "Field",
      sortable: false,
      render: (value: string) => (
        <span className="font-medium">{value}</span>
      ),
    },
    {
      key: "original",
      header: "Original Bill Value",
      sortable: false,
      render: (value: string) => (
        <span className="text-gray-500">{value}</span>
      ),
    },
    {
      key: "new",
      header: "New Value",
      sortable: false,
      render: (value: string, row: DataField) => (
        <Input
          value={value}
          onChange={(e) => handleUpdate(row.id, e.target.value)}
          placeholder={`Enter new ${row.label.toLowerCase()}`}
          className="h-8"
        />
      ),
    },
  ];

  return (
    <Card className="border-cyan-100 shadow-sm overflow-hidden">
      <CardHeader className="bg-cyan-50/50 border-b border-cyan-100 p-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <FileText className="h-5 w-5 text-cyan-600" />
          </div>
          <div>
            <CardTitle className="">Data Correction</CardTitle>
            <p className="card-subheading-text">Update consumer details</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5" />
          <p className="text-sm text-blue-700">
            Note: These consumer data changes will be updated in consumer master
            data.
          </p>
        </div>

        <div className="border rounded-md overflow-hidden bg-white">
          <GenericTable
            data={dataFields}
            columns={columns}
            rowKey="id"
            emptyMessage="No data fields available"
          />
        </div>

        <div className="mt-6">
          <Label htmlFor="data-reason">Reason for Update<span className="text-red-500">*</span></Label>
          <Input
            id="data-reason"
            value={reason}
            onChange={(e) => handleReasonChange(e.target.value)}
            placeholder="Why is this data being changed?"
            className="mt-2"
          />
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <Button
            disabled={!reason}
            onClick={handleSave}
            className={cn(
              "min-w-[140px] transition-all duration-200",
              isSaved
                ? "bg-green-600 hover:bg-green-700 text-white shadow-md"
                : "bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm"
            )}
          >
            {isSaved ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Correction"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// TemplateCorrection component (updated)
function TemplateCorrection({ 
  onSave,
  onFormEdit,
  isSaved,
  savedData,
  billDetails,
  correctionId,
}: CorrectionProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [isInitialized, setIsInitialized] = useState(false);
  const isUpdatingRef = useRef(false);
  const {data: templates} = useBillTemplates(remoteUtilityId);
  const templateDropdown = mapToLabelValue(templates, "configName", "template");
  const {mutate: changeTemplate} = usePatchBillCorrection()
  const getFormFields = (): FormField[] => {
    return [
      {
        name: "currentTemplate",
        label: "Current Template",
        type: "text",
        required: false,
        disabled: true,
        className: "col-span-1",
      },
      {
        name: "newTemplate",
        label: "New Template",
        type: "select",
        required: true,
        options: templateDropdown,
        placeholder: "Select new template",
        className: "col-span-1",
      },
      {
        name: "reason",
        label: "Reason for Template Change",
        type: "textarea",
        required: true,
        placeholder: "e.g., Customer requested detailed breakdown format...",
        className: "col-span-2",
      }
    ];
  };

  const form = FormService.createForm({
    fields: getFormFields(),
    mode: "onChange",
    validateOnMount: false,
  });

  // Initialize from saved data
  useEffect(() => {
    isUpdatingRef.current = true;
    
    // Set current template (always show default)
    form.setValue("currentTemplate", billDetails?.templateName, { shouldValidate: false });
    
    if (savedData) {
      // Populate from saved data
      if (savedData.template) {
        form.setValue("newTemplate", savedData.template, { shouldValidate: false });
      }
      if (savedData.reason) {
        form.setValue("reason", savedData.reason, { shouldValidate: false });
      }
    }
    
    isUpdatingRef.current = false;
    
    // Mark as initialized after a short delay
    setTimeout(() => {
      setIsInitialized(true);
    }, 100);
  }, [savedData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    if (isUpdatingRef.current) {
      return;
    }
    
    // Trigger edit callback when field changes (only after initialization)
    if (isInitialized && isSaved) {
      onFormEdit();
    }
  };

  const handleSave = async () => {
    const isValid = await FormService.validateForm(form);
    
    if (isValid) {
      const formData = form.getValues();
      const data = {
        template_id: formData.newTemplate,
        reason: formData.reason
      };
      changeTemplate({
        id: correctionId,
        type: 'template',
        payload: data
      })
      onSave(data);
    }
  };

  const selectedTemplate = form.watch("newTemplate");

  return (
    <Card className="border-indigo-100 shadow-sm overflow-hidden">
      <CardHeader className="bg-indigo-50/50 border-b border-indigo-100 p-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <LayoutTemplate className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <CardTitle className="">Change Bill Template</CardTitle>
            <p className="card-subheading-text">
              Update the visual layout of the bill
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="">
        <DynamicForm
          fields={getFormFields()}
          form={form}
          gridColumns={2}
          config={{
            layout: "vertical",
            spacing: "medium",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={(field, value) => handleFieldChange(field, value)}
        />

        {!selectedTemplate && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md flex items-start gap-2 text-sm text-gray-600">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Please select a new template from the dropdown above.</span>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <Button
            onClick={handleSave}
            className={cn(
              "min-w-[140px] transition-all duration-200",
              isSaved
                ? "bg-green-600 hover:bg-green-700 text-white shadow-md"
                : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
            )}
          >
            {isSaved ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Correction"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}